import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, BimModell } from '../api';
import { ContentSection, DataTable, EmptyState, type Spalte } from '../muster';
import type { Rahmen } from './Projektrahmen';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SEITE „BIM-MODELLE“ (BIM12, Owner F147/F148, Vertrag 17 §8 Vorschlag 1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Das Modellregister des BIM-Kerns (`bim_modell`) eines Projekts - NUR LESEND.
 * Es führt jede Quelle der RVT-Modellaufnahme (b1 → b3): die eigenen
 * TGA-Modelle, Sandbox-Modelle und jede verknüpfte Fremddatei (Architektur,
 * Tragwerk, …), auch eine, die beim Lauf nicht geladen war.
 *
 * WARUM EINE EIGENE LISTE UND NICHT DIE MODELLLISTE
 * -------------------------------------------------
 * Die Modellliste (`models`) speist Zeitpläne, die ACC-Zuordnung, die
 * Auftragsanlage und die Vergabe. Ein Fremdmodell dort würde nachts geöffnet
 * und mit Fachaufnahmen belegt (Vertrag 17 V1/V2). Deshalb erscheinen die
 * Fremdmodelle „eigens“ (F147) hier - ohne jede Wirkung auf diese Abläufe.
 *
 * Die Seite steht nur in der Navigation, wenn der Server den BIM-Kern
 * freigeschaltet hat (`Projekt.bimModelle`, Schalter `Bim:Schatten`). Ohne
 * Schalter antwortet der Endpunkt 404; die Seite zeigt dann dessen Meldung.
 */

/** Anzeige der Modellrolle (Werteliste Vertrag 17 §6). Unbekannte Werte bleiben roh stehen. */
const ROLLEN: Record<string, { text: string; rang: number }> = {
  eigen_tga: { text: 'TGA', rang: 0 },
  sandbox: { text: 'Sandbox', rang: 1 },
  koordination: { text: 'Koordination', rang: 2 },
  referenz: { text: 'Referenz', rang: 3 },
  fremd: { text: 'Fremd', rang: 4 },
  unbestimmt: { text: 'unbestimmt', rang: 5 },
};

export function rolleText(rolle: string): string {
  return ROLLEN[rolle]?.text ?? rolle;
}

export function rolleRang(rolle: string): number {
  return ROLLEN[rolle]?.rang ?? 9;
}

/** Anzeigename: der Dokumenttitel, sonst die Kennung (Titel ist nur Anzeige, nie Schlüssel). */
export function modellName(m: { titel: string | null; kennung: string }): string {
  return m.titel && m.titel.trim() !== '' ? m.titel : m.kennung;
}

/**
 * Reihenfolge der Liste: eigene Modelle zuerst, Fremdmodelle danach (je Rolle
 * ein Block), innerhalb einer Rolle nach Name.
 */
export function ordneModelle(modelle: readonly BimModell[]): BimModell[] {
  return [...modelle].sort((a, b) => rolleRang(a.rolle) - rolleRang(b.rolle)
    || modellName(a).localeCompare(modellName(b), 'de', { sensitivity: 'base' })
    || a.kennung.localeCompare(b.kennung));
}

const DATUM = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** „30.10.2026“ - leer bei einer Fehlstelle. */
export function datumText(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DATUM.format(d);
}

/** Ganze Zahl deutsch („12.345“), leer bei einer Fehlstelle. */
export function zahlText(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : n.toLocaleString('de-DE');
}

/** „verknüpft in“: die Host-Modelle (Links der aktiven Host-Version), sonst „entdeckt über …“. */
export function verknuepftText(m: BimModell): string {
  if (m.verknuepftIn.length > 0) return m.verknuepftIn.map(modellName).join(', ');
  return m.entdecktUeber ? `entdeckt über ${modellName(m.entdecktUeber)}` : '';
}

export function BimModelle({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [modelle, setModelle] = useState<BimModell[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);
  const [rolle, setRolle] = useState('');

  const holen = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    api.bimModelle(projektId)
      .then((liste) => setModelle(ordneModelle(liste)))
      .catch((e: unknown) => { setModelle([]); setFehler(e); })
      .finally(() => setLaedt(false));
  }, [api, projektId]);

  useEffect(() => { holen(); }, [holen]);

  // Die Rollen der Auswahl kommen aus dem Bestand, in der Listenreihenfolge.
  const rollen = useMemo(
    () => [...new Set(modelle.map((m) => m.rolle))].sort((a, b) => rolleRang(a) - rolleRang(b)),
    [modelle],
  );
  const zeilen = useMemo(
    () => (rolle ? modelle.filter((m) => m.rolle === rolle) : modelle),
    [modelle, rolle],
  );

  const spalten = useMemo<Spalte<BimModell>[]>(() => [
    { schluessel: 'name', titel: 'Modell', wert: modellName, immerSichtbar: true },
    {
      schluessel: 'rolle', titel: 'Rolle', titelHinweis: 'TGA = eigenes Registermodell, Fremd = verknüpfte Fremddatei',
      wert: (m) => rolleRang(m.rolle), inhalt: (m) => rolleText(m.rolle),
    },
    { schluessel: 'format', titel: 'Format', wert: (m) => m.dateiformat.toUpperCase() },
    {
      schluessel: 'version', titel: 'Aktive Version', art: 'zahl', ausrichtung: 'rechts',
      wert: (m) => m.aktiveVersion?.folge ?? null,
      inhalt: (m) => (m.aktiveVersion
        ? <span title={m.aktiveVersion.versionskennung}>{m.aktiveVersion.folge}</span>
        : <span className="m-leise">keine</span>),
    },
    {
      schluessel: 'datum', titel: 'Stand vom', art: 'datum',
      wert: (m) => (m.aktiveVersion ? new Date(m.aktiveVersion.datum) : null),
      inhalt: (m) => datumText(m.aktiveVersion?.datum),
    },
    {
      schluessel: 'bauteile', titel: 'Elemente', art: 'zahl', ausrichtung: 'rechts',
      titelHinweis: 'Bauteile (Instanzen) der aktiven Version, ohne Typen, Ebenen und Ansichten',
      wert: (m) => m.aktiveVersion?.bauteile ?? null, inhalt: (m) => zahlText(m.aktiveVersion?.bauteile),
    },
    {
      schluessel: 'verknuepft', titel: 'Verknüpft in', wert: verknuepftText,
      inhalt: (m) => (m.verknuepftIn.length > 0 || !m.entdecktUeber
        ? verknuepftText(m)
        : <span className="m-leise">{verknuepftText(m)}</span>),
    },
    {
      schluessel: 'elemente', titel: 'Elemente gesamt', art: 'zahl', ausrichtung: 'rechts', versteckt: true,
      titelHinweis: 'alle Elemente der aktiven Version, auch Typen, Ebenen, Räume, Systeme',
      wert: (m) => m.aktiveVersion?.elemente ?? null, inhalt: (m) => zahlText(m.aktiveVersion?.elemente),
    },
    {
      schluessel: 'parameterwerte', titel: 'Parameterwerte', art: 'zahl', ausrichtung: 'rechts', versteckt: true,
      wert: (m) => m.aktiveVersion?.parameterwerte ?? null, inhalt: (m) => zahlText(m.aktiveVersion?.parameterwerte),
    },
    {
      schluessel: 'versionskennung', titel: 'Versionskennung', versteckt: true,
      wert: (m) => m.aktiveVersion?.versionskennung ?? '',
    },
    { schluessel: 'kennung', titel: 'Kennung', wert: (m) => m.kennung, versteckt: true },
  ], []);

  return (
    <ContentSection
      titel="BIM-Modelle"
      beschreibung={'Alle Modelle der BIM-Aufnahme dieses Projekts - eigene TGA-Modelle und verknüpfte '
        + 'Fremdmodelle, nur lesend. Die Liste wirkt nicht auf Modellliste, Zeitpläne, ACC-Zuordnung oder Aufträge.'}
    >
      <DataTable<BimModell>
        name="BIM-Modelle"
        spalten={spalten}
        zeilen={zeilen}
        schluessel={(m) => m.id}
        laedt={laedt && modelle.length === 0}
        fehler={fehler}
        beiErneutVersuchen={holen}
        trefferText={`${zeilen.length.toLocaleString('de-DE')} von ${modelle.length.toLocaleString('de-DE')} Modellen`}
        suchePlatzhalter="Modell, Kennung, Host …"
        ausfuhr
        ausfuhrDateiname="bim-modelle.csv"
        leer={modelle.length === 0
          ? (
            <EmptyState
              titel="Noch keine BIM-Aufnahme."
              erklaerung={'Sobald die BIM-Aufnahme Modelle dieses Projekts in die Datenbank übernommen hat, '
                + 'stehen sie hier - die eigenen TGA-Modelle und jede verknüpfte Fremddatei.'}
              klein
            />
          )
          : undefined}
        werkzeuge={rollen.length > 1 && (
          <label>
            Rolle{' '}
            <select name="rolle" value={rolle} onChange={(e) => setRolle(e.target.value)}>
              <option value="">alle</option>
              {rollen.map((r) => <option key={r} value={r}>{rolleText(r)}</option>)}
            </select>
          </label>
        )}
      />
    </ContentSection>
  );
}

/** Die Route `/projekte/:id/bim-modelle` im Projektrahmen. */
export function BimModelleSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <div className="leer">wird geladen …</div>;
  return <BimModelle api={api} projektId={projekt.id} />;
}
